import {
  SUGGESTION_STATUSES,
  type SuggestionSummary,
  type SupportSummary,
  TICKET_KINDS,
  TICKET_STATUSES,
  TICKET_URGENCIES,
  type TicketStatus,
  type TicketSummary,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Award, LifeBuoy, Lightbulb, ThumbsUp } from 'lucide-react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { SUGGESTION_TONE, supportMeQuery, supportUi, TICKET_TONE } from '../../lib/support';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { Tabs } from '../psd/shared';
import { RewardsPanel, rewardsQuery } from './RewardsPanel';
import { SuggestionAnalysisPanel } from './SuggestionAnalysisPanel';
import { SupportReportPanel } from './SupportReportPanel';

type Tab = 'mine' | 'suggestions' | 'rewards' | 'queue' | 'analysis' | 'report';

/** Цвет статуса предложения — в lib/support.ts, чтобы его брали и вкладки, и карточка без круговых ссылок */
export { SUGGESTION_TONE };

export function SupportPage() {
  useCrumbs([]);
  const search = useSearch({ strict: false }) as { tab?: Tab; status?: string; period?: string };
  const nav = useNavigate();
  const me = useQuery(supportMeQuery);
  const isAgent = me.data?.isAgent ?? false;
  const tab: Tab =
    search.tab === 'suggestions' ||
    search.tab === 'rewards' ||
    ((search.tab === 'queue' || search.tab === 'report' || search.tab === 'analysis') && isAgent)
      ? search.tab
      : 'mine';
  const go = (patch: { tab?: Tab; status?: string | undefined; period?: string | undefined }) =>
    nav({
      to: '/support',
      search: { tab: patch.tab ?? tab, status: patch.status, period: patch.period } as never,
    });

  const tabs: Array<{ value: Tab; label: string }> = [
    { value: 'mine', label: 'Мои обращения' },
    { value: 'suggestions', label: 'Предложения' },
    { value: 'rewards', label: 'Премии' },
  ];
  if (isAgent)
    tabs.push(
      { value: 'queue', label: 'Очередь поддержки' },
      { value: 'analysis', label: 'Анализ предложений' },
      { value: 'report', label: 'Отчёт' },
    );

  return (
    <Page>
      <PageHeader
        title="Поддержка"
        subtitle="Вопросы, ошибки и предложения по порталу. Отвечает человек: до 2 часов, если работа стоит, и до 4 рабочих часов в остальных случаях"
        actions={
          <>
            <Button
              variant="primary"
              icon={<LifeBuoy className="size-4" />}
              onClick={() => supportUi.openDialog('ticket')}
            >
              Написать в поддержку
            </Button>
            <Button
              icon={<Lightbulb className="size-4" />}
              onClick={() => supportUi.openDialog('suggestion')}
            >
              Предложить улучшение
            </Button>
          </>
        }
      />
      <Tabs value={tab} onChange={(v) => go({ tab: v, status: undefined })} items={tabs} />
      {me.data && !isAgent && search.tab && search.tab !== tab ? (
        <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-[14px]">
          {search.tab === 'analysis'
            ? 'Анализ предложений'
            : search.tab === 'report'
              ? 'Отчёт поддержки'
              : 'Очередь поддержки'}{' '}
          — у специалистов поддержки и владельца портала. Ваши обращения — ниже, предложения коллег и премии —
          на соседних вкладках.
        </div>
      ) : null}
      {tab === 'report' ? (
        <SupportReportPanel />
      ) : tab === 'analysis' ? (
        <SuggestionAnalysisPanel />
      ) : tab === 'rewards' ? (
        <RewardsPanel period={search.period} onPeriod={(p) => go({ period: p })} />
      ) : tab === 'suggestions' ? (
        <Suggestions
          status={search.status}
          onStatus={(s) => go({ status: s })}
          onRewards={() => go({ tab: 'rewards', status: undefined })}
        />
      ) : (
        <Tickets
          scope={tab === 'queue' ? 'queue' : 'mine'}
          status={search.status}
          onStatus={(s) => go({ status: s })}
        />
      )}
    </Page>
  );
}

function SummaryTiles() {
  const q = useQuery({
    queryKey: ['support', 'summary'],
    queryFn: () => api<SupportSummary>('/support/summary'),
  });
  if (!q.data) return null;
  const d = q.data;
  const tiles = [
    { label: 'Открыто', value: String(d.open) },
    { label: 'Просрочен ответ', value: String(d.overdue) },
    { label: 'Без исполнителя', value: String(d.unassigned) },
    {
      label: 'Первый ответ, в среднем',
      value:
        d.avgFirstResponseMinutes === null ? 'нет ответов' : `${Math.round(d.avgFirstResponseMinutes)} мин`,
    },
    {
      label: 'Ответов в срок за 30 дней',
      value: d.inTimePercent === null ? 'нет ответов' : `${Math.round(d.inTimePercent)}%`,
    },
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-lg border border-line bg-surface p-3">
          <div className="text-[12px] text-ink-2">{t.label}</div>
          <div className="mt-1 text-[18px] leading-6 font-semibold">{t.value}</div>
        </div>
      ))}
    </div>
  );
}

function Tickets({
  scope,
  status,
  onStatus,
}: {
  scope: 'mine' | 'queue';
  status: string | undefined;
  onStatus: (s: string | undefined) => void;
}) {
  const q = useQuery({
    queryKey: ['support', 'tickets', scope, status ?? ''],
    queryFn: () => api<TicketSummary[]>('/support/tickets', { query: { scope, status } }),
  });
  return (
    <>
      {scope === 'queue' ? <SummaryTiles /> : null}
      <div className="mb-3 max-w-xs">
        <Select
          className="h-11 md:h-10"
          aria-label="Статус"
          value={status ?? ''}
          onChange={(e) => onStatus(e.target.value || undefined)}
        >
          <option value="">Все обращения</option>
          <option value="open">Открытые</option>
          <option value="closed">Закрытые</option>
          {Object.entries(TICKET_STATUSES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.length === 0 ? (
        <EmptyState title={scope === 'queue' ? 'Обращений нет' : 'Вы ещё не писали в поддержку'}>
          {scope === 'queue'
            ? 'Новые обращения появятся здесь и в «Ждёт меня».'
            : 'Нажмите «Написать в поддержку»: ответит человек, срок ответа виден в обращении.'}
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <ul className="divide-y divide-line">
            {q.data.map((t) => (
              <li key={t.id}>
                <Link
                  to="/support/tickets/$id"
                  params={{ id: t.id }}
                  className="flex min-h-11 flex-col gap-1 px-5 py-3 hover:bg-surface-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[12px] text-ink-2">{t.number}</span>
                    <Badge tone={TICKET_TONE[t.status]}>{TICKET_STATUSES[t.status as TicketStatus]}</Badge>
                    {t.urgency === 'blocking' ? (
                      <Badge tone="danger">{TICKET_URGENCIES.blocking}</Badge>
                    ) : null}
                    {t.overdue ? <Badge tone="danger">Ответ просрочен</Badge> : null}
                    {t.unread ? <Badge tone="accent">Есть ответ</Badge> : null}
                  </div>
                  <div className="text-[14px] font-medium [overflow-wrap:anywhere]">{t.subject}</div>
                  <div className="text-[12px] text-ink-2">
                    {TICKET_KINDS[t.kind]} · {scope === 'queue' ? `${t.authorName} · ` : ''}
                    {t.assigneeName ? `отвечает ${t.assigneeName} · ` : 'исполнителя нет · '}
                    {dateTime(t.createdAt)}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </>
  );
}

/** Полоса над предложениями: за лучшие предложения и активность — премии; мои баллы и место за период */
function RewardsBanner({ onOpen }: { onOpen: () => void }) {
  const q = useQuery(rewardsQuery());
  const v = q.data;
  if (!v?.rules.enabled) return null;
  return (
    <div className="mb-3 flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3 md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 items-start gap-2.5">
        <Award className="mt-0.5 size-5 shrink-0 text-accent-text" aria-hidden />
        <p className="text-[14px]">
          За лучшие предложения и активность — премии. Ваши баллы за {v.current.label}:{' '}
          <span className="num font-semibold">{v.me.points}</span>
          {v.me.place ? `, ${v.me.place}-е место из ${v.me.of}` : ''}.
        </p>
      </div>
      <Button size="sm" onClick={onOpen}>
        Как начисляются баллы
      </Button>
    </div>
  );
}

function Suggestions({
  status,
  onStatus,
  onRewards,
}: {
  status: string | undefined;
  onStatus: (s: string | undefined) => void;
  onRewards: () => void;
}) {
  const q = useQuery({
    queryKey: ['support', 'suggestions', status ?? ''],
    queryFn: () => api<SuggestionSummary[]>('/support/suggestions', { query: { status, sort: 'votes' } }),
  });
  return (
    <>
      <RewardsBanner onOpen={onRewards} />
      <div className="mb-3 max-w-xs">
        <Select
          className="h-11 md:h-10"
          aria-label="Статус"
          value={status ?? ''}
          onChange={(e) => onStatus(e.target.value || undefined)}
        >
          <option value="">Все предложения</option>
          <option value="open">Не закрытые</option>
          {Object.entries(SUGGESTION_STATUSES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.length === 0 ? (
        <EmptyState title="Предложений нет">
          Предложите, что сделать удобнее: коллеги смогут проголосовать.
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <ul className="divide-y divide-line">
            {q.data.map((s) => (
              <li key={s.id}>
                <Link
                  to="/support/suggestions/$id"
                  params={{ id: s.id }}
                  className="flex min-h-11 items-start justify-between gap-3 px-5 py-3 hover:bg-surface-2"
                >
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] text-ink-2">{s.number}</span>
                      <Badge tone={SUGGESTION_TONE[s.status]}>{SUGGESTION_STATUSES[s.status]}</Badge>
                      {s.unread ? <Badge tone="accent">Есть новое</Badge> : null}
                    </div>
                    <div className="text-[14px] font-medium [overflow-wrap:anywhere]">{s.title}</div>
                    <div className="text-[12px] text-ink-2">
                      {s.authorName} · {dateTime(s.createdAt)} · комментариев: {s.comments}
                    </div>
                  </div>
                  <span
                    className={
                      s.voted
                        ? 'inline-flex shrink-0 items-center gap-1 rounded-md bg-accent px-2 py-1 text-[13px] font-semibold text-on-accent'
                        : 'inline-flex shrink-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-[13px]'
                    }
                    title={s.voted ? 'Вы проголосовали' : 'Голосов'}
                  >
                    <ThumbsUp className="size-3.5" aria-hidden /> {s.votes}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </>
  );
}
